import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FileUpload from "../FileUploader/FileUpload";
import { API_URL } from "../../util/url";
import fetchWithAuth from "../../util/fetchWithAuth";
import AppDialog from "../ui/AppDialog";
import { PrimaryButton } from "../ui/PrimaryButton";
import FormTextField from "../ui/FormTextField";
import AttributesListModal from "../scripts/AttributesListModal";
import VariationsModal from "../scripts/VariationsModal";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";

const panelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  backgroundColor: "background.paper",
  p: { xs: 2, md: 3 },
};

const transformAttributes = (attributes) =>
  attributes.map((attribute) => ({
    name: attribute.name.trim(),
    values: attribute.variations.map((value) => ({ value })),
  }));

const parseNumber = (value) => Number(String(value).replace(",", "."));

function CreateScript({ onSubmint }) {
  const [attributes, setAttributes] = useState([]);
  const [colors, setColors] = useState([]);
  const [openColors, setOpenColors] = useState(false);
  const [openAttributes, setOpenAttributes] = useState(false);
  const [openVariations, setOpenVariations] = useState(false);
  const [currentAttrIndex, setCurrentAttrIndex] = useState(null);

  const [title, setTitle] = useState("");
  const [quantity, setQuantity] = useState("0");
  const [price, setPrice] = useState("");
  const [crm, setCrm] = useState("");
  const [productId, setProductId] = useState("");
  const [file, setFile] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: crmData } = useFetchWithAuth("/statistic/crm/");
  const crmOptions = useMemo(() => {
    if (Array.isArray(crmData)) return crmData;
    return Array.isArray(crmData?.results) ? crmData.results : [];
  }, [crmData]);

  const onFileSelect = (files) => {
    if (files?.length) setFile(files[0]);
  };

  const handleAddColor = () => {
    setColors((prev) => [
      ...prev,
      { id: Date.now(), name: "", hex: "#000000", quantity: "0" },
    ]);
  };

  const handleDeleteColor = (id) => {
    setColors((prev) => prev.filter((color) => color.id !== id));
  };

  const handleColorChange = (id, field, value) => {
    setColors((prev) =>
      prev.map((color) =>
        color.id === id ? { ...color, [field]: value } : color
      )
    );
  };

  const handleEditVariations = (index) => {
    setCurrentAttrIndex(index);
    setOpenVariations(true);
  };

  const validateForm = () => {
    const normalizedPrice = parseNumber(price);
    const normalizedQuantity = parseNumber(quantity);
    const normalizedProductId = parseNumber(productId);

    if (!title.trim()) return "Вкажіть назву товару.";
    if (!crm) return "Оберіть CRM.";
    if (!Number.isInteger(normalizedProductId) || normalizedProductId <= 0) {
      return "ID товару має бути додатним цілим числом.";
    }
    if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
      return "Вкажіть коректну ціну товару.";
    }
    if (!Number.isInteger(normalizedQuantity) || normalizedQuantity < 0) {
      return "Кількість має бути цілим числом від 0.";
    }

    const invalidColor = colors.find((color) => {
      const colorQuantity = parseNumber(color.quantity);
      return (
        !color.name.trim() ||
        !/^#[0-9a-f]{6}$/i.test(color.hex) ||
        !Number.isInteger(colorQuantity) ||
        colorQuantity < 0
      );
    });
    if (invalidColor) {
      return "Перевірте назву, HEX і кількість кожного кольору.";
    }

    const invalidAttribute = attributes.find((attribute) => !attribute.name.trim());
    if (invalidAttribute) return "Вкажіть назву кожного атрибута.";

    return null;
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setSaving(true);
    setSaveError("");

    const normalizedPrice = parseNumber(price);
    const normalizedQuantity = parseNumber(quantity);
    const normalizedProductId = parseNumber(productId);
    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("quantity", String(normalizedQuantity));
    formData.append("price", normalizedPrice.toFixed(2));
    formData.append("product_id", String(normalizedProductId));
    formData.append("crm", String(crm));
    formData.append(
      "attributes",
      JSON.stringify(transformAttributes(attributes))
    );
    formData.append(
      "colors",
      JSON.stringify(
        colors.map((color) => ({
          hex: color.hex,
          name: color.name.trim(),
          quantity: parseNumber(color.quantity),
        }))
      )
    );
    if (file) formData.append("image", file);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/scripts/products/create-full/`,
        { method: "POST", body: formData }
      );

      if (!response.ok) {
        let message = "Не вдалося зберегти товар.";
        try {
          const payload = await response.json();
          message = payload.detail || payload.error || message;
        } catch {
          // Keep a useful fallback for non-JSON server errors.
        }
        throw new Error(message);
      }

      const data = await response.json();
      onSubmint?.(data);
    } catch (error) {
      setSaveError(error.message || "Не вдалося зберегти товар.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSave}
      sx={{ maxWidth: 1220, mx: "auto", pb: 4 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            type="button"
            onClick={() => window.history.back()}
            aria-label="Назад"
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Додати товар
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Додайте товар до CRM і налаштуйте його характеристики.
            </Typography>
          </Box>
        </Box>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />
          }
          sx={{ minWidth: 132, textTransform: "none" }}
        >
          {saving ? "Збереження..." : "Зберегти товар"}
        </Button>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 340px) 1fr" },
          gap: 3,
        }}
      >
        <Box sx={panelSx}>
          <Typography variant="subtitle1" fontWeight={700}>
            Зображення
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Додайте основне зображення товару.
          </Typography>
          <Box
            sx={{
              height: 260,
              border: "1px dashed",
              borderColor: "primary.light",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "grey.50",
            }}
          >
            <FileUpload
              onFileSelect={onFileSelect}
              onFileRemove={() => setFile(null)}
              multiple={false}
              accept="image/*"
            />
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            Підтримуються зображення у форматах JPG, PNG або WEBP.
          </Typography>
        </Box>

        <Box sx={panelSx}>
          <Typography variant="subtitle1" fontWeight={700}>
            Основна інформація
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
            Поля з позначкою * є обов’язковими.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Назва товару"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="ID товару в CRM"
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              required
              fullWidth
            />
            <TextField
              label="Ціна"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              required
              fullWidth
            />
            <TextField
              label="Кількість"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel id="create-script-crm-label">CRM</InputLabel>
              <Select
                labelId="create-script-crm-label"
                label="CRM"
                value={crm}
                onChange={(event) => setCrm(event.target.value)}
              >
                <MenuItem value="" disabled>
                  Оберіть CRM
                </MenuItem>
                {crmOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {!crmOptions.length && (
                <FormHelperText>CRM поки недоступні.</FormHelperText>
              )}
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Додаткові дані
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<TuneOutlinedIcon />}
              onClick={() => setOpenAttributes(true)}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              Атрибути{attributes.length ? ` (${attributes.length})` : ""}
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<PaletteOutlinedIcon />}
              onClick={() => setOpenColors(true)}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              Кольори{colors.length ? ` (${colors.length})` : ""}
            </Button>
          </Stack>
        </Box>
      </Box>

      <AppDialog
        open={openColors}
        onClose={() => setOpenColors(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">Кольори товару</Typography>
          {!colors.length && (
            <Typography variant="body2" color="text.secondary">
              Додайте колір, щоб він з’явився у списку.
            </Typography>
          )}
          {colors.map((color) => (
            <Box
              key={color.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 90px 110px auto" },
                gap: 1,
                alignItems: "center",
              }}
            >
              <FormTextField
                label="Назва"
                value={color.name}
                onChange={(event) =>
                  handleColorChange(color.id, "name", event.target.value)
                }
              />
              <FormTextField
                label="HEX"
                type="color"
                value={color.hex}
                onChange={(event) =>
                  handleColorChange(color.id, "hex", event.target.value)
                }
              />
              <FormTextField
                label="Кількість"
                type="number"
                value={color.quantity}
                onChange={(event) =>
                  handleColorChange(color.id, "quantity", event.target.value)
                }
              />
              <IconButton
                type="button"
                color="error"
                onClick={() => handleDeleteColor(color.id)}
                aria-label="Видалити колір"
              >
                <span aria-hidden="true">×</span>
              </IconButton>
            </Box>
          ))}
          <Stack direction="row" spacing={1}>
            <PrimaryButton onClick={handleAddColor}>Додати колір</PrimaryButton>
            <Button onClick={() => setOpenColors(false)}>Готово</Button>
          </Stack>
        </Box>
      </AppDialog>

      <AppDialog
        open={openAttributes}
        onClose={() => setOpenAttributes(false)}
        maxWidth="sm"
        fullWidth
      >
        <AttributesListModal
          onClose={() => setOpenAttributes(false)}
          attributes={attributes}
          setAttributes={setAttributes}
          onEditVariations={handleEditVariations}
        />
      </AppDialog>

      <AppDialog
        open={openVariations}
        onClose={() => setOpenVariations(false)}
        maxWidth="sm"
        fullWidth
      >
        <VariationsModal
          open={openVariations}
          onClose={() => setOpenVariations(false)}
          attributes={attributes}
          setAttributes={setAttributes}
          attrIndex={currentAttrIndex}
        />
      </AppDialog>
    </Box>
  );
}

export default CreateScript;
